import { api } from '../lib/api.js';

export interface LiveAttendanceRow {
  employeeId: string;
  employeeCode: string;
  name: string;
  photoUrl: string | null;
  department: string | null;
  /** The department's id, for the rail's department cut — the name is display. */
  departmentId?: string | null;
  /** Job title — the fourth fact in the shared identity block. */
  position: string | null;
  /**
   * THE TWO HALVES OF THE ROSTER SCOPE, so this board can cut All / Workers /
   * Staff / Remote exactly as the employees list and the schedule do.
   *
   * Two fields and not one because Remote is not a group: three of the
   * switcher's four values are `Employee.workforceType`, and Remote is HOW
   * somebody punches (`attendanceProfile = SELF_ANYWHERE`). A remote person is
   * still Staff. `lib/rosterScope.ts` owns that split; these are what it reads.
   *
   * OPTIONAL for the same reason the four unaccounted fields below are: an
   * `apps/web` build can reach an API that predates them, and in that window a
   * scope filter must narrow nothing rather than hide everyone.
   */
  workforceType?: 'SITE_WORKER' | 'OFFICE_STAFF' | null;
  attendanceProfile?: string | null;
  status: 'IN' | 'OUT' | 'NOT_PUNCHED';
  since: string | null;
  worksite: string | null;
  /**
   * What "where are they" means for this person. Three populations, three
   * different answers, one column — see the Where column on the live board.
   */
  locationMode: 'ASSIGNED_SITE' | 'ANY_WORKSITE' | 'ANYWHERE';
  /** Where the last punch was taken; null when the device gave no fix. */
  lat: number | null;
  lng: number | null;
  /**
   * Status of the most recent event today. Read it through `needsReview`
   * below rather than comparing to 'FLAGGED': PENDING_APPROVAL — where
   * `field.punchGuards.ts` lands an unpromoted verify-mode face scan — is a
   * punch a supervisor has to look at for exactly the same reason, and the
   * boards used to let it through as if it were clean. REJECTED never
   * arrives: a rejected punch did not happen, so the feed omits it.
   */
  lastEventStatus: 'VALID' | 'FLAGGED' | 'PENDING_APPROVAL' | null;
  lastFlagReason: string | null;
  /** ID of the most recent event today — used by the override dialog. */
  lastEventId: string | null;
  /**
   * ─── THE FOUR FIELDS THE "UNACCOUNTED" OBSERVATION READS ──────────────────
   *
   * All four are OPTIONAL, and that is not laziness. An `apps/web` build can
   * reach an API that predates them (the demo host deploys the two halves
   * separately), and in that window every one of these is `undefined`. The
   * `?` makes the compiler force that case to be handled, and `exceptions.ts`
   * handles it by making NO claim rather than a guess — design-rules §3.3,
   * "we don't know" is not "nothing is wrong".
   */

  /**
   * WHAT the last event was. `status` cannot answer this: `'OUT'` covers a
   * PUNCH_OUT, a camp PUNCH_IN (arriving at accommodation), a BREAK_START and
   * a BREAK_END alike, and only the first of those means "left, arrived
   * nowhere".
   */
  lastEventType?: 'PUNCH_IN' | 'PUNCH_OUT' | 'BREAK_START' | 'BREAK_END' | null;
  /**
   * The TYPE of the worksite the last punch was taken at — sent because the
   * NAME cannot be joined back to a type safely (names are not unique) and
   * because leaving a camp and leaving a site are different events. Null when
   * the punch matched no worksite, e.g. remote staff.
   */
  worksiteType?:
    | 'OFFICE'
    | 'CONSTRUCTION'
    | 'CAMP'
    | 'RETAIL'
    | 'WAREHOUSE'
    | 'REMOTE'
    | 'OTHER'
    | null;
  /**
   * This person's working day as "HH:MM" in the COMPANY timezone, already
   * resolved server-side through `employee.workStartTime ??
   * settings.defaultWorkday.shiftStartTime` — so it is per-person wherever an
   * override exists, and readable by a MANAGER, who is locked out of
   * `/settings/defaultWorkday`. Null when neither end supplied a value.
   */
  shiftStart?: string | null;
  shiftEnd?: string | null;
}

/**
 * DOES A HUMAN HAVE TO LOOK AT THIS ROW?
 *
 * Every board in the console asked this as `lastEventStatus === 'FLAGGED'`,
 * in eleven places, which was the whole answer while FLAGGED was the only
 * status a punch could carry besides VALID. It is not: `field.punchGuards.ts`
 * lands an unpromoted `verify`-mode FACE punch on PENDING_APPROVAL — a scan
 * the model was not confident about — and those rows were reaching the
 * supervisor's board drawn exactly like a clean one, absent from the flagged
 * filter and absent from the review queue's count. The `movementFeed` service
 * had already grouped the two statuses; the boards had not.
 *
 * One predicate so they cannot drift again. Both states resolve through the
 * same override (`overrideEventStatus` takes any event id and writes VALID or
 * REJECTED), so admitting the second costs the review flow nothing.
 */
export function needsReview(row: {
  lastEventStatus?: LiveAttendanceRow['lastEventStatus'];
}): boolean {
  return row.lastEventStatus === 'FLAGGED' || row.lastEventStatus === 'PENDING_APPROVAL';
}

export const liveApi = {
  list: (scope?: 'all' | 'team') =>
    api
      .get<{ data: LiveAttendanceRow[] }>('/attendance/live', {
        params: scope ? { scope } : undefined,
      })
      .then((r) => r.data),
  /**
   * Manager / HR / Admin override of a flagged event. Backend enforces
   * team-scope on MANAGER; UI surfaces the action only on FLAGGED rows.
   */
  overrideEventStatus: (
    eventId: string,
    payload: { status: 'VALID' | 'REJECTED'; reason: string }
  ) =>
    api
      .patch<{ data: { id: string; status: string } }>(
        `/attendance/events/${eventId}/status`,
        payload
      )
      .then((r) => r.data),
};
